import {
  fetchLoadActiveUserCount,
  fetchLoadDeepTestCount,
  fetchLoadReviewCount,
  fetchLoadSimpleMeasureCount,
  fetchLoadSimpleTestCount,
  fetchLoadUserTotalCount,
} from "api/admin/dashboard";
import { useEffect, useMemo, useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LineChart,
  Tooltip,
  Line,
  Legend,
  Bar,
  BarChart,
} from "recharts";
import { ChartBox, ChartItemWrap } from "style/admin/dashboard";
import { formatDateOnly, getPreviousRange } from "utils/dashboard.util";
import { format, getDay } from "date-fns";
import { ko } from "date-fns/locale";
import { PeriodOption } from "./dashboard.select";

interface DataProps {
  selectBox: string;
  startDate: string;
  endDate: string;
  period: PeriodOption;
}

interface DailyCountItem {
  date: string;
  count: number;
}

const DashboardMultiChartLine = ({
  selectBox,
  startDate,
  endDate,
  period,
}: DataProps) => {
  const [chartData, setChartData] = useState<DailyCountItem[]>([]);
  const [prevChartData, setPrevChartData] = useState<DailyCountItem[]>([]);

  const fetchMap: Record<string, any> = {
    newUser: fetchLoadUserTotalCount,
    activeUser: fetchLoadActiveUserCount,
    measure: fetchLoadSimpleMeasureCount,
    simpleTest: fetchLoadSimpleTestCount,
    deepTest: fetchLoadDeepTestCount,
    review: fetchLoadReviewCount,
  };

  useEffect(() => {
    if (!startDate || !endDate || !selectBox || !period) return;

    const fetchByDateRange = async () => {
      const start = new Date(startDate);
      const end = new Date(endDate);

      const dateList: string[] = [];
      const temp = new Date(start);

      while (temp <= end) {
        dateList.push(formatDateOnly(temp));
        temp.setDate(temp.getDate() + 1);
      }

      const { prevStart, prevEnd } = getPreviousRange(
        startDate,
        endDate,
        period
      );

      const prevDateList: string[] = [];
      const tempPrev = new Date(prevStart);
      while (tempPrev <= new Date(prevEnd)) {
        prevDateList.push(formatDateOnly(tempPrev));
        tempPrev.setDate(tempPrev.getDate() + 1);
      }

      const fetchFn = fetchMap[selectBox];
      if (!fetchFn) return;

      const requests = dateList.map(async (date) => {
        const startDt = `${date} 00:00:00`;
        const endDt = `${date} 23:59:59`;

        const res = await fetchFn({
          searchStartDt: startDt,
          searchEndDt: endDt,
        });

        const count =
          res?.result?.reduce?.((sum: number, group: any) => {
            return (
              sum +
              (group.maleCount || 0) +
              (group.femaleCount || 0) +
              (group.count || 0)
            );
          }, 0) ?? 0;

        return { date, count };
      });

      const prevRequests = prevDateList.map(async (date) => {
        const res = await fetchFn({
          searchStartDt: `${date} 00:00:00`,
          searchEndDt: `${date} 23:59:59`,
        });

        const count =
          res?.result?.reduce?.((sum: number, group: any) => {
            return (
              sum +
              (group.maleCount || 0) +
              (group.femaleCount || 0) +
              (group.count || 0)
            );
          }, 0) ?? 0;

        return { date, count };
      });

      try {
        const [currResult, prevResult] = await Promise.all([
          Promise.all(requests),
          Promise.all(prevRequests),
        ]);

        setChartData(currResult);
        setPrevChartData(prevResult);
      } catch (err) {
        console.error("차트 데이터 조회 실패:", err);
      }
    };

    fetchByDateRange();
  }, [selectBox, startDate, endDate]);

  const totalCount = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.count, 0);
  }, [chartData]);

  const mergedData = useMemo(() => {
    if (period === "weekly" || period === "daily") {
      const weekMap = new Map<number, any>();

      chartData.forEach(({ date, count }) => {
        const dayIndex = getDay(new Date(date));
        const dayLabel = format(new Date(date), "eee", { locale: ko });

        weekMap.set(dayIndex, {
          ...(weekMap.get(dayIndex) || {}),
          dayIndex,
          day: dayLabel,
          current: count,
        });
      });

      prevChartData.forEach(({ date, count }) => {
        const dayIndex = getDay(new Date(date));
        const dayLabel = format(new Date(date), "eee", { locale: ko });

        weekMap.set(dayIndex, {
          ...(weekMap.get(dayIndex) || {}),
          dayIndex,
          day: dayLabel,
          previous: count,
        });
      });

      return Array.from(weekMap.values()).sort(
        (a, b) => a.dayIndex - b.dayIndex
      );
    }

    // ✅ 월간일 경우: 날짜별로 표현
    return chartData.map((item, index) => {
      return {
        date: item.date,
        current: item.count,
        previous: prevChartData[index]?.count || 0,
      };
    });
  }, [chartData, prevChartData, period]);

  const currentName = period === "monthly" ? "이번달" : "이번주";
  const previousName = period === "monthly" ? "지난달" : "지난주";

  return (
    <ChartBox>
      <ChartItemWrap>
        <h4>일자별 비교</h4>
        <ResponsiveContainer width="100%" height={300}>
          {period === "daily" ? (
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: -30, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="0 3" />
              <XAxis
                dataKey="date"
                tickMargin={10}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return isNaN(date.getTime())
                    ? value
                    : format(date, "MM/dd", { locale: ko });
                }}
              />
              <YAxis
                domain={
                  totalCount === 0 ? [0, 1] : [0, Math.ceil(totalCount * 1.1)]
                }
                tick={{ fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip
                formatter={(value: number, name: string) => [
                  `${value}명`,
                  name === "count" ? "이번주" : "지난주",
                ]}
                contentStyle={{ fontSize: "12px" }}
              />
              <Bar dataKey="count" name="이번주" fill="#5B93FF" />
            </BarChart>
          ) : (
            <LineChart
              data={mergedData}
              margin={{ top: 20, right: 30, left: -30, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="0 3" />
              <XAxis
                dataKey={period === "monthly" ? "date" : "day"}
                tickMargin={10}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) =>
                  period === "monthly"
                    ? format(new Date(value), "MM/dd")
                    : value
                }
              />
              <YAxis
                domain={
                  totalCount === 0 ? [0, 1] : [0, Math.ceil(totalCount * 1.1)]
                }
                tick={{ fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip contentStyle={{ fontSize: "12px" }} />
              <Line
                type="monotone"
                dataKey="previous"
                name={previousName}
                stroke="#FF8E5B"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="current"
                name={currentName}
                stroke="#5B93FF"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Legend iconSize={16} />
            </LineChart>
          )}
        </ResponsiveContainer>
      </ChartItemWrap>
    </ChartBox>
  );
};

export default DashboardMultiChartLine;
